import React from 'react';
import { useTranslation } from 'react-i18next';
import { CreditCard, Smartphone, AlertCircle } from 'lucide-react';

/**
 * Multi-provider payment method selector.
 *
 * Props:
 *   providers: [{ id, display_name, icon, supported_currencies, supports_subscriptions, supports_one_time, configured }]
 *   value: currently selected provider id
 *   onChange: (providerId) => void
 *   mode: "subscription" | "one_time"  → filters providers by capability
 *   currency: currency code (lowercase, e.g. "eur") to filter providers that support it
 *
 * A provider is DISABLED if:
 *   - it doesn't support the required mode (subscription vs one_time), OR
 *   - it doesn't support the requested currency, OR
 *   - it isn't configured yet (dry-run).
 *
 * Aucune API n'est appelée ici. Le composant est 100% présentation.
 */

const PROVIDER_LOGO = {
    stripe: (
        <div className="w-10 h-10 rounded-md bg-gradient-to-br from-[#635BFF] to-[#4A44C6] flex items-center justify-center text-white shadow-sm">
            <CreditCard className="w-5 h-5" />
        </div>
    ),
    paypal: (
        <div className="w-10 h-10 rounded-md bg-[#003087] flex items-center justify-center shadow-sm">
            <span className="text-white font-bold text-sm font-['Outfit']">P</span>
        </div>
    ),
    orange_money: (
        <div className="w-10 h-10 rounded-md bg-[#FF6600] flex items-center justify-center text-white shadow-sm">
            <Smartphone className="w-5 h-5" />
        </div>
    ),
};

export default function PaymentProviderSelector({
    providers,
    value,
    onChange,
    mode = 'subscription',
    currency = 'eur',
}) {
    const { t } = useTranslation();

    if (!Array.isArray(providers) || providers.length === 0) {
        return (
            <div
                data-testid="payment-provider-empty"
                className="p-4 rounded-lg border border-dashed border-[#E5E7EB] bg-[#F9FAFB] text-sm text-[#6B7280]"
            >
                {t('payment.no_provider_available', { defaultValue: 'Aucun fournisseur de paiement disponible pour le moment.' })}
            </div>
        );
    }

    const needsSub = mode === 'subscription';

    const items = providers.map((p) => {
        const capabilityOK = needsSub ? p.supports_subscriptions : p.supports_one_time;
        const currencyOK = !currency || (p.supported_currencies || []).includes((currency || '').toLowerCase());
        const disabledReasons = [];
        if (!p.configured) disabledReasons.push(t('payment.not_configured', { defaultValue: 'Non configuré' }));
        if (!capabilityOK) {
            disabledReasons.push(needsSub
                ? t('payment.no_subscription', { defaultValue: 'Ne supporte pas les abonnements' })
                : t('payment.no_one_time', { defaultValue: 'Ne supporte pas les paiements ponctuels' }));
        }
        if (!currencyOK) disabledReasons.push(t('payment.currency_unsupported', { defaultValue: 'Devise non supportée', currency }));
        return { ...p, disabled: disabledReasons.length > 0, disabledReasons };
    });

    return (
        <div className="grid gap-3" data-testid="payment-provider-selector">
            {items.map((p) => {
                const selected = value === p.id;
                return (
                    <button
                        key={p.id}
                        type="button"
                        disabled={p.disabled}
                        onClick={() => !p.disabled && onChange(p.id)}
                        data-testid={`payment-provider-${p.id}`}
                        aria-pressed={selected}
                        aria-disabled={p.disabled}
                        className={`
                            group relative flex items-center gap-4 p-4 rounded-lg border-2 text-left w-full transition-all
                            ${selected && !p.disabled
                                ? 'border-[#002FA7] bg-[#EEF2FF] shadow-sm'
                                : p.disabled
                                    ? 'border-[#E5E7EB] bg-[#F9FAFB] opacity-60 cursor-not-allowed'
                                    : 'border-[#E5E7EB] bg-white hover:border-[#002FA7]/40 hover:bg-[#F9FAFB]'}
                        `}
                    >
                        {PROVIDER_LOGO[p.id] || (
                            <div className="w-10 h-10 rounded-md bg-[#F3F4F6] flex items-center justify-center">
                                <CreditCard className="w-5 h-5 text-[#6B7280]" />
                            </div>
                        )}
                        <div className="flex-1">
                            <div className="font-semibold text-[#0A0A0A]">{p.display_name}</div>
                            <div className="text-xs text-[#6B7280] mt-0.5">
                                {(p.supported_currencies || []).map((c) => c.toUpperCase()).join(' · ')}
                            </div>
                            {p.disabled && (
                                <div className="mt-1 flex items-center gap-1 text-xs text-[#B45309]">
                                    <AlertCircle className="w-3 h-3" />
                                    {p.disabledReasons.join(' · ')}
                                </div>
                            )}
                        </div>
                        <div
                            className={`
                                w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0
                                ${selected && !p.disabled ? 'border-[#002FA7] bg-[#002FA7]' : 'border-[#D1D5DB] bg-white'}
                            `}
                        >
                            {selected && !p.disabled && (
                                <div className="w-2 h-2 rounded-full bg-white" />
                            )}
                        </div>
                    </button>
                );
            })}
        </div>
    );
}
